/**
 * TextBuffer - 文本缓冲层，用于流式更新时的批量处理
 * 
 * 功能：
 * - 累积chunk到缓冲区
 * - 使用节流机制批量更新
 * - 支持立即刷新
 * - 支持JSON结构化数据存储和解析
 * - 1.2.21: 添加快照和回退功能（借鉴StreamParser的状态管理）
 */

import type { StructuredMessage } from '../types/structuredMessage';
import { parseStructuredMessage } from './structuredParser';
import type { ChunkTag } from '../types/chat';

export interface TextBufferOptions {
  /** 节流间隔（毫秒），默认100ms */
  throttleMs?: number;
  /** 最大缓冲区大小（字符数），默认无限制 */
  maxBufferSize?: number;
}

export type FlushCallback = (text: string) => void;

// 1.2.21: 快照数据结构（借鉴StreamParser的状态管理）
interface Snapshot {
  sequence: number;
  timestamp: number;
  content: string;
  isComplete: boolean;
}

export class TextBuffer {
  private buffer: string = '';
  private pendingFlush: number | null = null;
  private readonly throttleMs: number;
  private readonly maxBufferSize: number;
  private flushCallback: FlushCallback | null = null;
  /** JSON结构化数据（在流式完成后解析） */
  private structuredData: StructuredMessage | null = null;
  
  // 1.2.21: 快照管理（借鉴StreamParser的状态管理机制）
  private snapshots: Snapshot[] = [];
  private lastCompleteSnapshot: Snapshot | null = null;
  private readonly maxSnapshots = 10; // 最多保留10个快照，避免内存泄漏
  private accumulatedContent: string = ''; // 累积的完整内容

  constructor(options: TextBufferOptions = {}) {
    this.throttleMs = options.throttleMs ?? 100;
    this.maxBufferSize = options.maxBufferSize ?? Infinity;
  }

  /**
   * 设置刷新回调函数
   */
  setFlushCallback(callback: FlushCallback): void {
    this.flushCallback = callback;
  }

  /**
   * 添加文本chunk到缓冲区
   * 1.2.21: 支持chunk标签和快照管理
   */
  append(chunk: string, tag?: ChunkTag, isComplete?: boolean): void {
    if (!chunk) return;

    // 检查缓冲区大小限制
    if (this.buffer.length + chunk.length > this.maxBufferSize) {
      // 如果超过限制，先刷新现有内容
      this.flush();
    }

    this.buffer += chunk;
    this.accumulatedContent += chunk;

    // 1.2.21: 如果chunk标记为完整，保存快照
    if (isComplete && tag) {
      this.saveSnapshot(tag, this.accumulatedContent, true);
    } else if (tag) {
      // 即使不完整也保存快照（用于回退）
      this.saveSnapshot(tag, this.accumulatedContent, false);
    }

    // 调度节流更新
    this.scheduleFlush();
  }

  /**
   * 调度刷新（节流）
   */
  private scheduleFlush(): void {
    // 如果已经有待处理的刷新，取消它
    if (this.pendingFlush !== null) {
      cancelAnimationFrame(this.pendingFlush);
    }

    // 使用 requestAnimationFrame 实现平滑更新
    this.pendingFlush = requestAnimationFrame(() => {
      // 使用 setTimeout 实现节流
      setTimeout(() => {
        this.flush();
        this.pendingFlush = null;
      }, this.throttleMs);
    });
  }

  /**
   * 立即刷新缓冲区
   */
  flush(): void {
    if (this.buffer.length === 0) return;

    const textToFlush = this.buffer;
    this.buffer = '';

    // 取消待处理的刷新
    if (this.pendingFlush !== null) {
      cancelAnimationFrame(this.pendingFlush);
      this.pendingFlush = null;
    }

    // 调用回调函数
    if (this.flushCallback) {
      this.flushCallback(textToFlush);
    }
  }

  /**
   * 获取当前缓冲区内容（不刷新）
   */
  getBuffer(): string {
    return this.buffer;
  }

  /**
   * 清空缓冲区
   */
  clear(): void {
    this.buffer = '';
    if (this.pendingFlush !== null) {
      cancelAnimationFrame(this.pendingFlush);
      this.pendingFlush = null;
    }
  }

  /**
   * 解析结构化数据
   * 在流式完成后调用，将缓冲区文本解析为JSON结构化数据
   */
  parseStructured(): StructuredMessage | null {
    if (this.buffer.length === 0) {
      return this.structuredData;
    }

    // 解析缓冲区内容为结构化数据
    this.structuredData = parseStructuredMessage(this.buffer);
    return this.structuredData;
  }

  /**
   * 获取结构化数据（不解析）
   */
  getStructuredData(): StructuredMessage | null {
    return this.structuredData;
  }

  /**
   * 设置结构化数据
   */
  setStructuredData(data: StructuredMessage): void {
    this.structuredData = data;
  }

  /**
   * 1.2.21: 保存快照（借鉴StreamParser的状态管理）
   */
  private saveSnapshot(tag: ChunkTag, content: string, isComplete: boolean): void {
    const snapshot: Snapshot = {
      sequence: tag.sequence,
      timestamp: tag.timestamp,
      content: content,
      isComplete: isComplete
    };

    this.snapshots.push(snapshot);

    // 如果标记为完整，更新lastCompleteSnapshot
    if (isComplete) {
      this.lastCompleteSnapshot = snapshot;
    }

    // 限制快照数量，避免内存泄漏
    if (this.snapshots.length > this.maxSnapshots) {
      this.snapshots.shift(); // 移除最旧的快照
    }
  }

  /**
   * 1.2.21: 回退到上一个完整状态（借鉴StreamParser的回退机制）
   */
  rollbackToLastComplete(): { success: boolean; content?: string; snapshot?: Snapshot } {
    if (!this.lastCompleteSnapshot) {
      return { success: false };
    }

    // 回退到最后一个完整快照
    this.accumulatedContent = this.lastCompleteSnapshot.content;
    this.buffer = ''; // 清空当前buffer，使用快照内容

    // 移除lastCompleteSnapshot之后的所有快照
    const lastCompleteIndex = this.snapshots.findIndex(
      s => s.sequence === this.lastCompleteSnapshot!.sequence
    );
    if (lastCompleteIndex !== -1) {
      this.snapshots = this.snapshots.slice(0, lastCompleteIndex + 1);
    }

    return {
      success: true,
      content: this.lastCompleteSnapshot.content,
      snapshot: this.lastCompleteSnapshot
    };
  }

  /**
   * 1.2.21: 获取最后一个完整快照
   */
  getLastCompleteSnapshot(): Snapshot | null {
    return this.lastCompleteSnapshot;
  }

  /**
   * 1.2.21: 获取所有快照（用于调试）
   */
  getSnapshots(): Snapshot[] {
    return [...this.snapshots];
  }

  /**
   * 1.2.21: 获取累积内容
   */
  getAccumulatedContent(): string {
    return this.accumulatedContent;
  }

  /**
   * 销毁缓冲区，清理资源
   */
  destroy(): void {
    this.clear();
    this.flushCallback = null;
    this.structuredData = null;
    // 1.2.21: 清理快照
    this.snapshots = [];
    this.lastCompleteSnapshot = null;
    this.accumulatedContent = '';
  }
}

